/**
 * Custom hook for managing game state
 *
 * Uses the shared storage system for persistence
 */

import { useState, useCallback } from 'react';
import type { GameState, GameStats, Shape, Settings } from '../utils/types';
import {
  loadHighScore,
  saveHighScore,
  loadSettings,
  saveSettings,
} from '../storage/gameStorage';

export interface UseGameStateReturn {
  gameState: GameState;
  stats: GameStats;
  currentShape: Shape | null;
  settings: Settings;
  isAnimating: boolean;
  startGame: () => void;
  endGame: () => void;
  nextLevel: () => void;
  setCurrentShape: (shape: Shape | null) => void;
  addScore: (points: number) => void;
  addStrike: () => void;
  setAnimating: (animating: boolean) => void;
  updateSettings: (newSettings: Partial<Settings>) => void;
  resetGame: () => void;
}

export function useGameState(): UseGameStateReturn {
  const [gameState, setGameState] = useState<GameState>('menu');
  const [stats, setStats] = useState<GameStats>({
    level: 1,
    score: 0,
    strikes: 0,
    highScore: loadHighScore(),
  });
  const [currentShape, setCurrentShape] = useState<Shape | null>(null);
  const [settings, setSettingsState] = useState<Settings>(loadSettings());
  const [isAnimating, setIsAnimating] = useState(false);

  const startGame = useCallback(() => {
    setGameState('playing');
    setStats(prev => ({
      level: 1,
      score: 0,
      strikes: 0,
      highScore: prev.highScore,
    }));
    setCurrentShape(null);
    setIsAnimating(false);
  }, []);

  const endGame = useCallback(() => {
    setGameState('gameOver');
    setCurrentShape(null);
    setIsAnimating(false);

    // Update high score if needed
    setStats(prev => {
      if (prev.score > prev.highScore) {
        const newHighScore = prev.score;
        saveHighScore(newHighScore);
        return { ...prev, highScore: newHighScore };
      }
      return prev;
    });
  }, []);

  const nextLevel = useCallback(() => {
    setStats(prev => ({ ...prev, level: prev.level + 1 }));
    setGameState('playing');
  }, []);

  const addScore = useCallback((points: number) => {
    setStats(prev => ({ ...prev, score: prev.score + points }));
  }, []);

  const addStrike = useCallback(() => {
    setStats(prev => {
      const newStrikes = prev.strikes + 1;
      return { ...prev, strikes: newStrikes };
    });
  }, []);

  const setAnimating = useCallback((animating: boolean) => {
    setIsAnimating(animating);
    if (animating) {
      setGameState('animating');
    } else {
      setGameState('playing');
    }
  }, []);

  const updateSettings = useCallback((newSettings: Partial<Settings>) => {
    setSettingsState(prev => {
      const updated = { ...prev, ...newSettings };
      saveSettings(updated);
      return updated;
    });
  }, []);

  const resetGame = useCallback(() => {
    setGameState('menu');
    setStats(prev => ({
      level: 1,
      score: 0,
      strikes: 0,
      highScore: prev.highScore,
    }));
    setCurrentShape(null);
    setIsAnimating(false);
  }, []);

  return {
    gameState,
    stats,
    currentShape,
    settings,
    isAnimating,
    startGame,
    endGame,
    nextLevel,
    setCurrentShape,
    addScore,
    addStrike,
    setAnimating,
    updateSettings,
    resetGame,
  };
}
